"use client";

import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import {
  createPaginationURL,
  updateURLWithoutReload,
  calculateTotalPages,
  normalizePage,
  normalizePageSize,
} from "@/lib/utils";
import type { SortOrder } from "@/types";

interface UseListPaginationProps<T, S extends string | null = SortOrder> {
  initialItems: T[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  sortable?: boolean;
  initialSort?: S | null;
  onPageChange: (
    page: number,
    pageSize: number,
    sortBy?: S | null
  ) => Promise<{ items: T[]; total: number }>;
  /**
   * The sort the page's SERVER derives from a raw `?sort=` value — pass the
   * parser the page itself uses, so an absent or invalid value yields its
   * default. Opts the list into restoring `?sort=` and an explicit
   * `?pageSize=` from the URL when the server props go stale (Back from a
   * detail page). Without it only `?page=` is restored.
   */
  parseSort?: (raw: string | null) => S | null;
}

interface UseListPaginationReturn<T, S extends string | null = SortOrder> {
  items: T[];
  currentPage: number;
  currentSort: S | null | undefined;
  pageSize: number;
  totalPages: number;
  isPending: boolean;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  handlePageChange: (newPage: number) => void;
  handleSortChange?: (newSort: S | null) => void;
  handlePageSizeChange?: (newPageSize: number) => void;
}

/**
 * Universal pagination hook for list views
 * Supports both sortable and non-sortable lists
 *
 * @example
 * // With sorting
 * const { items, currentPage, handleSortChange } = useListPagination({
 *   initialItems: games,
 *   initialPage: 1,
 *   initialTotal: 1000,
 *   pageSize: 50,
 *   sortable: true,
 *   initialSort: 'asc',
 *   onPageChange: loadGames,
 * });
 *
 * @example
 * // Without sorting
 * const { items, currentPage } = useListPagination({
 *   initialItems: topSellers,
 *   initialPage: 1,
 *   initialTotal: 500,
 *   pageSize: 50,
 *   onPageChange: loadTopSellers,
 * });
 */
export function useListPagination<T, S extends string | null = SortOrder>({
  initialItems,
  initialPage,
  initialTotal,
  pageSize,
  sortable = false,
  initialSort,
  onPageChange,
  parseSort,
}: UseListPaginationProps<T, S>): UseListPaginationReturn<T, S> {
  const [items, setItems] = useState(initialItems);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentSort, setCurrentSort] = useState<S | null | undefined>(
    initialSort
  );
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [total, setTotal] = useState(initialTotal);
  const [isPending, startTransition] = useTransition();
  const requestSeq = useRef(0);

  // Зберігаємо завжди актуальний onPageChange у ref, щоб loadItems не перестворювався кожен рендер
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  const totalPages = calculateTotalPages(total, currentPageSize);

  const loadItems = useCallback(
    async (
      page: number,
      sort?: S | null,
      options?: { updateUrl?: boolean; pageSize?: number }
    ) => {
      const seq = ++requestSeq.current;
      const effectivePageSize = options?.pageSize ?? currentPageSize;

      // URL update — imperative (history.pushState), без re-render; робимо одразу.
      if (options?.updateUrl !== false) {
        const existingSearch =
          typeof window !== "undefined" ? window.location.search : "";
        updateURLWithoutReload(
          createPaginationURL(
            page,
            sortable ? (sort as unknown as string | null) ?? null : null,
            existingSearch,
            effectivePageSize
          )
        );
      }

      try {
        const result = await onPageChangeRef.current(page, effectivePageSize, sort);
        // Захист від "гонок": якщо прийшла застаріла відповідь, ігноруємо її.
        if (seq !== requestSeq.current) return;
        setItems(result.items);
        if (result.total > 0) {
          setTotal(result.total);
        }
      } catch (error) {
        if (seq !== requestSeq.current) return;
        console.error("Failed to load items:", error);
      }
    },
    // onPageChange навмисно не в deps — використовуємо ref, щоб завжди мати актуальний callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPageSize, sortable]
  );

  // Sync state from props when the parent re-renders with new server data
  // (e.g. searchParams-driven refresh on /monthly's MonthPicker, /upcoming ?q=,
  // or a filter router navigation). Deps on initialItems — the array prop's ref
  // changes only when the server returned new data, so this doesn't re-fire on
  // client-only renders.
  //
  // It ALSO reconciles the list state with the URL. Pagination and sort update
  // the URL via history.pushState (loadItems → updateURLWithoutReload), which
  // Next's router never sees — so Back into the list from a detail page serves
  // the cached page-1 / default-sort RSC even though the address bar still
  // says ?page=N&sort=X. We honor the URL: show its page and fetch it. Doing
  // this INSIDE the sync (rather than a separate effect) keeps a single source
  // of truth and is idempotent under React Strict Mode's double mount — no
  // second effect can reset it. `?sort=` and `?pageSize=` need the page's own
  // defaults, or an omitted param would read as a change: sort is restored
  // only with `parseSort` (the page's parser), and pageSize only alongside it
  // and only when the URL states one.
  useEffect(() => {
    setItems(initialItems);
    setTotal(initialTotal);
    // Reset seq — any in-flight loadItems (e.g. a pending "next page") is now
    // ignored; the server-side data is authoritative.
    requestSeq.current++;

    const params =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null;
    const urlPage = params
      ? normalizePage(params.get("page") ?? undefined)
      : initialPage;
    setCurrentPage(urlPage);

    if (!params || !parseSort) {
      setCurrentSort(initialSort);
      if (urlPage !== initialPage) {
        startTransition(async () => {
          await loadItems(urlPage, initialSort, { updateUrl: false });
        });
      }
      return;
    }

    const urlSort = sortable ? parseSort(params.get("sort")) : initialSort;
    const rawPageSize = params.get("pageSize");
    // Without an explicit param the server used its default — `pageSize`.
    const urlPageSize = rawPageSize ? normalizePageSize(rawPageSize) : pageSize;
    setCurrentSort(urlSort);
    setCurrentPageSize(urlPageSize);
    if (
      urlPage !== initialPage ||
      (urlSort ?? null) !== (initialSort ?? null) ||
      urlPageSize !== pageSize
    ) {
      startTransition(async () => {
        await loadItems(urlPage, urlSort, {
          updateUrl: false,
          pageSize: urlPageSize,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems]);

  // Синхронізація стану з URL при навігації Back/Forward (popstate),
  // щоб не було розсинхрону UI vs адресний рядок.
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const pageFromUrl = normalizePage(params.get("page") ?? undefined);
      const sortFromUrl = params.get("sort");
      const pageSizeFromUrl = normalizePageSize(params.get("pageSize"));
      const nextSort = sortable
        ? parseSort
          ? parseSort(sortFromUrl)
          : ((sortFromUrl ?? null) as unknown as S | null)
        : null;

      const sortChanged = sortable
        ? (nextSort ?? null) !== (currentSort ?? null)
        : false;
      const pageSizeChanged = pageSizeFromUrl !== currentPageSize;

      if (pageFromUrl === currentPage && !sortChanged && !pageSizeChanged)
        return;

      // Urgent: синхронізуємо UI state з URL миттєво (пагінація/sort одразу реагують
      // на back/forward). Async fetch — в transition, щоб isPending тримав skeleton.
      setCurrentPage(pageFromUrl);
      if (pageSizeChanged) setCurrentPageSize(pageSizeFromUrl);
      if (sortable && sortChanged) setCurrentSort(nextSort);

      startTransition(async () => {
        await loadItems(pageFromUrl, nextSort, {
          updateUrl: false,
          pageSize: pageSizeFromUrl,
        });
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // Нам важливі актуальні currentPage/currentSort в замиканні
  }, [currentPage, currentSort, currentPageSize, loadItems, sortable, parseSort]);

  const handlePageChange = (newPage: number) => {
    const newTotalPages = calculateTotalPages(total, currentPageSize);
    if (newPage < 1 || newPage > newTotalPages || newPage === currentPage) {
      return;
    }

    // Urgent optimistic update: пагінація миттєво підсвічує target page.
    // Async fetch — в transition, щоб isPending тримав skeleton до відповіді.
    setCurrentPage(newPage);
    startTransition(async () => {
      await loadItems(newPage, currentSort);
    });
  };

  const handleSortChange = sortable
    ? (newSort: S | null) => {
        setCurrentPage(1);
        setCurrentSort(newSort);
        startTransition(async () => {
          await loadItems(1, newSort);
        });
      }
    : undefined;

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize === currentPageSize) return;
    // При зміні pageSize завжди повертаємось на сторінку 1.
    setCurrentPage(1);
    setCurrentPageSize(newPageSize);
    startTransition(async () => {
      await loadItems(1, currentSort, { pageSize: newPageSize });
    });
  };

  const goToNextPage = () => handlePageChange(currentPage + 1);
  const goToPrevPage = () => handlePageChange(currentPage - 1);

  return {
    items,
    currentPage,
    currentSort,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    goToNextPage,
    goToPrevPage,
    handlePageChange,
    handleSortChange,
    handlePageSizeChange,
  };
}
